import { useI18n } from '../../i18n/useI18n'
import {
  CANCELLATION_HANDLING_FEE_PCT,
  PREPAID_REFUND_TIERS,
  prepaidRefundAmount,
} from './reservationPolicy'

interface Props {
  /** 表示する文脈に応じてバリエーション切替。
   * - 'modal': 予約モーダル内の簡易表示
   * - 'detail': コース詳細ページの本格表示
   */
  variant: 'modal' | 'detail'
  /** 該当する決済モード行のみハイライトしたいときに渡す */
  highlightedMode?: 'onsite' | 'deposit' | 'prepaid' | null
  /** キャンセル料保証が**実際に**効くか (= 当日払い行の表示分岐)。店の保存値ではなく
   * `reservationPaymentRules(store).noShowProtection` を渡す (#2026 現金のみの店は効かない) */
  onsiteProtected?: boolean
  /** #1371 予約総額。渡すと前払いの実際の返金額を金額で出す */
  totalAmount?: number | null
}

/**
 * #737 顧客側キャンセル料規定の共通表示。
 * 食べログ準拠でモード別に段階返金率を明示。
 * 規約 docs/legal/refund-policy.md の内容と整合させること。
 */
export default function CancellationPolicyTable({ variant, highlightedMode, onsiteProtected, totalAmount }: Props) {
  const { t } = useI18n()
  const isOnsiteProtected = !!onsiteProtected

  const rows: { mode: 'onsite' | 'deposit' | 'prepaid'; titleKey: string; descKey: string; tone: string }[] = [
    {
      mode: 'prepaid',
      titleKey: 'cancellation.prepaidTitle',
      descKey: 'cancellation.prepaidDesc',
      tone: 'red',
    },
    {
      mode: 'deposit',
      titleKey: 'cancellation.depositTitle',
      descKey: 'cancellation.depositDesc',
      tone: 'amber',
    },
    {
      mode: 'onsite',
      titleKey: isOnsiteProtected ? 'cancellation.onsiteOnTitle' : 'cancellation.onsiteOffTitle',
      descKey: isOnsiteProtected ? 'cancellation.onsiteOnDesc' : 'cancellation.onsiteOffDesc',
      tone: isOnsiteProtected ? 'green' : 'gray',
    },
  ]

  // #1351 follow-up: **その店で実際に使われる決済モードの規定だけ**を出す。
  // 以前は 3 モードを常に並べており、客はその店で発生しない前払い/手付金の
  // キャンセル料まで読まされていた。自分に適用される条件が埋もれて、
  // 「いつまでなら無料か」という一番知りたい情報が伝わらない。
  //
  // highlightedMode は呼び出し側 (ReservationModal / CourseDetail) が
  // `reservationPaymentRules(store).paymentTiming` を渡しており、その店の実モードそのもの。
  // #2026 保存値 (`reservationConfig.paymentTiming`) を渡すと、現金のみの店で前払いが出る。
  // 未指定のときだけ従来どおり全モードを並べる (比較目的の文脈を壊さない)。
  const visibleRows = highlightedMode
    ? rows.filter((row) => row.mode === highlightedMode)
    : rows

  const containerClass = variant === 'modal'
    ? 'bg-gray-50 rounded-lg p-3 text-xs'
    : 'bg-white border border-gray-100 rounded-xl p-4 text-sm'

  return (
    <div className={containerClass}>
      <p className={`font-display font-bold text-text-primary mb-2 ${variant === 'modal' ? 'text-sm' : 'text-base'}`}>
        {t('cancellation.title')}
      </p>
      <p className="font-body text-text-secondary mb-3 leading-relaxed text-[11px]">
        {t('cancellation.intro')}
      </p>

      {/* #1371 金額が確定している予約では**実額**を出す。率だけでは客が
        * 「いくら戻るか」を判断できない。手続き料も金額で明示し、
        * 「全額返金と書いてあるのに満額戻らない」を起こさない。 */}
      {totalAmount != null && totalAmount > 0 && highlightedMode === 'prepaid' && (
        <div className="mb-3 rounded-md bg-white/70 border border-gray-200 p-2.5">
          <p className="font-body text-[11px] font-semibold text-text-primary mb-1.5">
            {t('cancellation.actualTitle', { total: totalAmount.toLocaleString() })}
          </p>
          <div className="space-y-0.5">
            {PREPAID_REFUND_TIERS.map((tier) => {
              const refund = prepaidRefundAmount(totalAmount, tier.pct)
              return (
                <div key={tier.labelKey} className="flex items-center justify-between gap-2">
                  <span className="font-body text-[11px] text-text-secondary">
                    {t(tier.labelKey)}
                  </span>
                  <span className="font-body text-[11px] text-text-primary">
                    {refund > 0
                      ? t('cancellation.actualRefund', { amount: refund.toLocaleString() })
                      : t('cancellation.actualNoRefund')}
                  </span>
                </div>
              )
            })}
          </div>
          <p className="font-body text-[10px] text-text-tertiary mt-1.5 leading-relaxed">
            {t('cancellation.handlingFeeNote', { pct: String(CANCELLATION_HANDLING_FEE_PCT) })}
          </p>
          {/* #1804 この表はカード前提の金額。**支払い方法を選ぶのはこの後**なので、
              ポイントなら手続き料がかからないことをここで言っておく。言わないと
              「¥7,144 と書いてあったのに ¥7,600 戻った」と食い違って見える */}
          <p className="font-body text-[10px] text-matcha mt-1 leading-relaxed">
            {t('cancellation.pointsNoHandlingFee')}
          </p>
        </div>
      )}
      <div className="space-y-2">
        {visibleRows.map((row) => {
          const isHighlighted = highlightedMode === row.mode
          const toneBorder = {
            red: 'border-l-yokoso-red',
            amber: 'border-l-amber-500',
            green: 'border-l-green-600',
            gray: 'border-l-gray-400',
          }[row.tone] as string
          return (
            <div
              key={row.mode}
              className={`rounded-md border ${toneBorder} border-l-4 p-2.5 ${
                isHighlighted ? 'border-yokoso-red bg-yokoso-red/5' : 'border-gray-200 bg-white'
              }`}
            >
              <p className="font-body text-xs font-semibold text-text-primary">
                {t(row.titleKey)}
                {/* 1 行だけ出しているときは「あなたの選択」バッジが自明で冗長。
                    比較表示 (highlightedMode 未指定) のときだけ付ける。 */}
                {isHighlighted && visibleRows.length > 1 && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] bg-yokoso-red text-white">
                    {t('cancellation.yourSelection')}
                  </span>
                )}
              </p>
              <p className="font-body text-xs text-text-secondary mt-1 leading-relaxed whitespace-pre-line">
                {t(row.descKey)}
              </p>
            </div>
          )
        })}
      </div>

      {/* #1373 上の表はすべて**お客様都合**の規定。店舗都合で取り消された場合に
        * 同じ率が適用されると誤解されるため、全額返金であることを明記する。
        * 実装は _refund_all_on_store_cancellation (段階返金を適用しない)。 */}
      <p className="font-body text-[11px] text-text-secondary mt-2.5 leading-relaxed">
        {t('cancellation.storeCancelFullRefund')}
      </p>
    </div>
  )
}
